import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getDeployments, createDeployment, getClients, getCandidates } from '../services/api';
import { UserCheck, Plus, Search, X, MapPin, Calendar } from 'lucide-react';
import toast from 'react-hot-toast';

const statusColor = (s) =>
  s === 'active'     ? 'bg-green-50 text-green-700' :
  s === 'completed'  ? 'bg-gray-50 text-gray-500' :
  s === 'terminated' ? 'bg-red-50 text-red-600' :
  s === 'on_hold'    ? 'bg-amber-50 text-amber-700' :
  'bg-blue-50 text-blue-700';

const AddDeploymentModal = ({ onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    candidate_id: '', client_id: '',
    designation: '', location: '',
    employment_type: 'contractual',
    start_date: '', end_date: '',
    pay_rate: '', bill_rate: '',
    working_days: 26, working_hours: 8,
    rate_unit: 'month'
  });

  const { data: clientsData } = useQuery({
    queryKey: ['clients-list'],
    queryFn : () => getClients({ limit: 100 }).then(r => r.data)
  });

  const { data: candidatesData } = useQuery({
    queryKey: ['candidates-list'],
    queryFn : () => getCandidates({ limit: 100, status: 'active' }).then(r => r.data)
  });

  const mutation = useMutation({
    mutationFn: createDeployment,
    onSuccess: () => {
      toast.success('Deployment created successfully!');
      queryClient.invalidateQueries(['deployments']);
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to create deployment')
  });

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate({
      ...form,
      pay_rate     : parseFloat(form.pay_rate),
      bill_rate    : parseFloat(form.bill_rate),
      working_days : parseInt(form.working_days),
      working_hours: parseInt(form.working_hours),
      end_date     : form.end_date || null
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-screen overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">New Deployment</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Candidate *</label>
            <select name="candidate_id" value={form.candidate_id} onChange={handle} required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Select Candidate</option>
              {candidatesData?.data?.map(c => (
                <option key={c.id} value={c.id}>
                  {c.first_name} {c.last_name} — {c.phone}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
            <select name="client_id" value={form.client_id} onChange={handle} required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Select Client</option>
              {clientsData?.data?.map(c => (
                <option key={c.id} value={c.id}>{c.company_name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
              <input name="designation" value={form.designation} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Machine Operator" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input name="location" value={form.location} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Chennai Plant" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type *</label>
            <select name="employment_type" value={form.employment_type} onChange={handle}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="contractual">Contractual</option>
              <option value="permanent">Permanent</option>
              <option value="temporary">Temporary</option>
              <option value="part_time">Part Time</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
              <input name="start_date" type="date" value={form.start_date} onChange={handle} required
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input name="end_date" type="date" value={form.end_date} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pay Rate (₹/month) *</label>
              <input name="pay_rate" type="number" value={form.pay_rate} onChange={handle} required
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. 15000" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bill Rate (₹/month) *</label>
              <input name="bill_rate" type="number" value={form.bill_rate} onChange={handle} required
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. 18000" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Working Days/Month</label>
              <input name="working_days" type="number" value={form.working_days} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="1" max="31" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Working Hours/Day</label>
              <input name="working_hours" type="number" value={form.working_hours} onChange={handle}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="1" max="24" />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={mutation.isPending}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {mutation.isPending ? 'Saving...' : 'Create Deployment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Deployments = () => {
  const navigate = useNavigate();
  const [search,  setSearch]  = useState('');
  const [status,  setStatus]  = useState('active');
  const [showAdd, setShowAdd] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['deployments', search, status],
    queryFn : () => getDeployments({ search, status, limit: 20 }).then(r => r.data)
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Deployments</h1>
          <p className="text-sm text-gray-400 mt-0.5">Track all deployed workers</p>
        </div>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
          <Plus size={16} />
          New Deployment
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or deployment number..."
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <select value={status} onChange={e => setStatus(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="terminated">Terminated</option>
          <option value="on_hold">On Hold</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading deployments...</div>
        ) : data?.data?.length === 0 ? (
          <div className="text-center py-12">
            <UserCheck size={32} className="mx-auto text-gray-200 mb-3" />
            <p className="text-gray-400 text-sm">No deployments yet. Deploy your first candidate!</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Candidate</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Client</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Designation</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Location</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Start Date</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Pay Rate</th>
                <th className="text-left text-xs font-medium text-gray-400 px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data?.data?.map(dep => (
                <tr key={dep.id} onClick={() => navigate(`/deployments/${dep.id}`)}
                  className="hover:bg-gray-50 transition cursor-pointer">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 text-xs font-semibold">
                          {dep.candidates?.first_name?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {dep.candidates?.first_name} {dep.candidates?.last_name}
                        </p>
                        <p className="text-xs text-gray-400">{dep.deployment_no}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-600">
                    {dep.clients?.company_name}
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-600">
                    {dep.designation || '—'}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <MapPin size={13} className="text-gray-400" />
                      {dep.location || '—'}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Calendar size={13} className="text-gray-400" />
                      {dep.start_date?.slice(0, 10)}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-600">
                    ₹{Number(dep.pay_rate).toLocaleString('en-IN')}
                  </td>
                  <td className="px-5 py-4">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColor(dep.status)}`}>
                      {dep.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && <AddDeploymentModal onClose={() => setShowAdd(false)} />}
    </div>
  );
};

export default Deployments;